import React, { useState, useMemo } from "react";
import { DollarSign, Users } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PageHeader } from "@shared/components/PageHeader";
import { useForm } from "react-hook-form";
import { ConsumersFilter } from "./ConsumersFilter";
import { Badge } from "@shared/ui/badge";
import { useParams } from "react-router-dom";
import {
  useRemoteUtilityId,
  useCurrency,
} from "@shared/selectors/globalSelectors";
import { useBulkRequestConsumersList } from "@features/cx/disconnect/hooks";
import { PAGE_SIZE } from "@shared/utils/constants";

interface ConsumerRow {
  id: string;
  accountNumber: string;
  name: string;
  phone: string;
  serviceArea: string;
  premise: string;
  plan: string;
  category: string;
  status: string;
}

interface ConsumerFilters {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  subCategory?: string;
  serviceArea?: string;
  premise?: string;
}

const ConsumersTab = () => {
  const { id: batchId } = useParams<{ id: string }>();
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState<ConsumerFilters>({
    page: 1,
    limit: PAGE_SIZE,
    search: "",
    category: "",
    subCategory: "",
    serviceArea: "",
    premise: "",
  });

  const { data: consumersListResponse, isLoading } =
    useBulkRequestConsumersList({
      batchId: batchId || "",
      remoteUtilityId: Number(remoteUtilityId),
      page: filters.page || 1,
      limit: filters.limit || PAGE_SIZE,
      searchData: filters.search,
      category: filters.category,
      subCategory: filters.subCategory,
      premise: filters.premise,
    });

  const filteredData = useMemo(() => {
    if (!consumersListResponse?.results) return [];

    return consumersListResponse.results.map((item: any) => ({
      id: item.id?.toString(),
      accountNumber: item?.consumerNo || "N/A",
      name: `${item?.firstName || ""} ${item?.lastName || ""}`.trim() || "N/A",
      phone: item?.contactNo || "N/A",
      serviceArea: item?.territoryData?.service?.area || "N/A",
      premise: item?.territoryData?.service?.premise || "N/A",
      plan: item?.planDisplay || "N/A",
      category: item.categoryDisplay || "N/A",
      status: item.statusDisplay || "N/A",
    }));
  }, [consumersListResponse]);

  const form = useForm({
    defaultValues: {
      category: "",
      sub_category: "",
      premise: "",
    },
  });

  const columns: TableColumn<ConsumerRow>[] = [
    {
      header: "Account Number",
      key: "accountNumber",
    },
    {
      header: "Name",
      key: "name",
    },
    { header: "Phone", key: "phone" },
    { header: "Category", key: "category" },
    { header: "Service Area", key: "serviceArea" },
    {
      header: "Premise",
      key: "premise",
    },
    {
      key: "status",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (val) => {
          if (val === "COMPLETED") return "success";
          if (val === "IN PROGRESS") return "secondary";
          if (val === "PENDING") return "outline";
          return "default";
        },
        getClassName: (val) => {
          if (val === "IN PROGRESS")
            return "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50";
          if (val === "PENDING")
            return "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50";
          return "";
        },
      },
    },
  ];

  const handleApplyFilters = (data: any) => {
    setFilters((prev) => ({
      ...prev,
      category: data.category,
      subCategory: data.sub_category,
      premise: data.premise,
      page: 1,
    }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: PAGE_SIZE,
      search: "",
      category: "",
      subCategory: "",
      serviceArea: "",
      premise: "",
    });
    setPage(1);
    form.reset({
      category: "",
      sub_category: "",
      premise: "",
    });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleSearch = (searchTerm: string) => {
    setFilters((prev) => ({ ...prev, search: searchTerm, page: 1 }));
    setPage(1);
  };

  const totalItems = consumersListResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  const pagination = {
    currentPage: page,
    totalPages,
    totalItems,
    itemsPerPage: PAGE_SIZE,
    startItem: (page - 1) * PAGE_SIZE + 1,
    endItem: Math.min(page * PAGE_SIZE, totalItems),
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Metric Banner */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100 rounded-xl p-6 flex justify-between items-center shadow-sm">
        <div className="flex items-center gap-4">
          <div className="bg-emerald-600 p-3 rounded-2xl text-white shadow-lg shadow-emerald-100 flex items-center justify-center">
            <DollarSign size={24} />
          </div>
          <div>
            <p className="text-[10px] font-black text-emerald-800 uppercase tracking-widest opacity-70 mb-1">
              Total Reconnection Fees Collected
            </p>
            <h2 className="text-3xl font-black text-emerald-900 tracking-tighter">
              {currency} 0.00
            </h2>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <div className="flex items-center justify-between mx-1">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <Users className="w-5 h-5 text-indigo-600" />
          </div>
          <PageHeader title="Consumer Reconnection Requests" subtitle="" />
        </div>
        <p className="text-xs text-neutral-400 font-bold uppercase tracking-tight">
          {totalItems} reconnection requests found
        </p>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden shadow-sm">
        <div className="p-2">
          <GenericTable
            data={filteredData}
            columns={columns}
            rowKey="id"
            onSearch={handleSearch}
            searchPlaceholder="Search by account, name or phone..."
            pagination={pagination}
            onPageChange={handlePageChange}
            filterPanel={
              <ConsumersFilter
                form={form}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
              />
            }
            getRowClassName={() =>
              "text-xs h-14 border-b last:border-0 hover:bg-indigo-50/20 transition-colors"
            }
          />
        </div>
      </div>
    </div>
  );
};
export default ConsumersTab;
